import React, { useState, useEffect } from 'react';
import { Sparkles, Home, Search, ArrowLeft, AlertCircle } from 'lucide-react';
import TopHeader from './components/TopHeader';
import { NavLink } from 'react-router-dom';

export default function NotFound() {
  const [floatingSparkles, setFloatingSparkles] = useState([]);

  useEffect(() => {
    // Generate random floating sparkles
    const sparkles = Array.from({ length: 8 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 3,
      duration: 3 + Math.random() * 2
    }));
    setFloatingSparkles(sparkles);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 relative overflow-hidden">
      {/* Floating Sparkles Animation */}
      {floatingSparkles.map((sparkle) => (
        <div
          key={sparkle.id}
          className="absolute animate-float opacity-20"
          style={{
            left: `${sparkle.left}%`,
            animationDelay: `${sparkle.delay}s`,
            animationDuration: `${sparkle.duration}s`
          }}
        >
          <Sparkles className="w-6 h-6 text-purple-400" />
        </div>
      ))}

      {/* Header */}
      <TopHeader />

      {/* Main 404 Content */}
      <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 pt-20">
        <div className="max-w-3xl w-full text-center">
          {/* Animated 404 Number */}
          <div className="relative mb-8">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-64 h-64 bg-gradient-to-r from-purple-400 to-blue-400 rounded-full blur-3xl opacity-20 animate-pulse"></div>
            </div>
            <div className="relative">
              <h1 className="text-9xl md:text-[12rem] font-bold bg-gradient-to-r from-purple-600 via-blue-600 to-purple-600 bg-clip-text text-transparent animate-gradient">
                404
              </h1>
            </div>
          </div>

          {/* Error Message */}
          <div className="mb-8 space-y-4">
            <div className="flex items-center justify-center gap-3 mb-4">
              <AlertCircle className="w-8 h-8 text-purple-600" />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Oops! This page got cleaned away
              </h2>
            </div>
            <p className="text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto">
              We searched high and low (and even under the couch cushions), but this page seems to have vanished. 
              Maybe it's enjoying a spotless vacation?
            </p>
          </div>

          {/* Decorative Icon Card */}
          <div className="mb-12 flex justify-center">
            <div className="relative bg-gradient-to-br from-white to-blue-50 rounded-3xl shadow-2xl p-8 transform hover:scale-105 transition-all inline-block">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-blue-400 rounded-3xl blur-2xl opacity-20"></div>
              <div className="relative flex items-center gap-4">
                <Sparkles className="w-16 h-16 text-purple-600" />
                <div className="text-left">
                  <p className="text-2xl font-bold text-gray-800">Don't worry!</p>
                  <p className="text-gray-600">We'll help you find your way</p>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            <button className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:shadow-2xl transition-all transform hover:scale-105 w-full sm:w-auto">
              <Home className="w-5 h-5" />
              Back to Home
            </button>
            <button className="flex items-center gap-2 bg-white text-gray-700 px-8 py-4 rounded-full text-lg font-semibold hover:shadow-xl transition-all transform hover:scale-105 border-2 border-gray-200 w-full sm:w-auto">
              <Search className="w-5 h-5" />
              Browse Services
            </button>
          </div>

          {/* Quick Links */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-8 max-w-2xl mx-auto">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Popular Pages</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { label: 'Book a Cleaning', icon: Sparkles, target:"/book" },
                { label: 'Our Services', icon: Home, target:"/" },
                { label: 'Manage Bookings', icon: Search, target: "/manage" },
                //{ label: 'Contact Us', icon: AlertCircle }
              ].map((link, index) => (
                <NavLink to={link.target}>
                <button
                  key={index}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl bg-gradient-to-r from-purple-50 to-blue-50 hover:from-purple-100 hover:to-blue-100 transition-all text-left group"
                >
                  <link.icon className="w-5 h-5 text-purple-600 group-hover:scale-110 transition-transform" />
                  <span className="text-gray-700 font-semibold">{link.label}</span>
                  <ArrowLeft className="w-4 h-4 text-gray-400 ml-auto transform rotate-180 group-hover:translate-x-1 transition-transform" />
                </button>
                </NavLink>
              ))}
            </div>
          </div>

          {/* Helpful Message */}
          <div className="mt-12 text-gray-500">
            <p className="mb-2">Lost? Need help?</p>
            <a href="mailto:hello@estherspotless.ca" className="text-purple-600 hover:text-purple-700 font-semibold">
              Contact us at hello@estherspotless.ca
            </a>
          </div>
        </div>
      </div>

      {/* Add custom animation styles */}
      <style jsx>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0) rotate(0deg);
          }
          50% {
            transform: translateY(-100vh) rotate(360deg);
          }
        }
        
        @keyframes gradient {
          0%, 100% {
            background-size: 200% 200%;
            background-position: left center;
          }
          50% {
            background-size: 200% 200%;
            background-position: right center;
          }
        }
        
        .animate-float {
          animation: float linear infinite;
        }
        
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 3s ease infinite;
        }
      `}</style>
    </div>
  );
}